import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { ErrorState } from '@/components/ui/States';
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton';
import { useCompanyRole } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { api, ApiError, type CompanyDetail, type WorkspaceDetail } from '@/lib/api';
import { LayoutHeader, TabBar, useCompanyCtx } from '../company/CompanyLayout';

export interface WorkspaceCtx {
  company: CompanyDetail;
  workspace: WorkspaceDetail;
  reloadWorkspace: () => void;
}

export const useWorkspaceCtx = () => useOutletContext<WorkspaceCtx>();

/** Resolves /c/:companySlug/w/:workspaceSlug — the slug→id hop uses the parent
 * company's workspaces array, already in outlet context. */
export function WorkspaceLayout() {
  const { workspaceSlug, projectSlug } = useParams<{ workspaceSlug: string; projectSlug?: string }>();
  const { company } = useCompanyCtx();
  // Tab visibility mirrors the API's permission table: Context is staff-only
  // (interviewee anonymity), Knowledge is staff + company admins, Settings
  // (connectors) is staff-only.
  const role = useCompanyRole(company.id);
  const isStaff = role === 'STAFF';

  const workspace = useAsync(async () => {
    const match = company.workspaces.find((w) => w.slug === workspaceSlug);
    if (!match) throw new ApiError('Workspace not found.', 404, 'not_found');
    return api.getWorkspace(match.id);
  }, [company.id, workspaceSlug]);

  if (workspace.status === 'loading') {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <SkeletonRows rows={3} />
      </div>
    );
  }
  if (workspace.status === 'error') {
    return <ErrorState error={workspace.error} onRetry={workspace.reload} />;
  }

  const ws = workspace.data;
  const base = `/c/${company.slug}/w/${ws.slug}`;
  const ctx = { company, workspace: ws, reloadWorkspace: workspace.reload } satisfies WorkspaceCtx;

  // Chrome belongs to the deepest active level only (see CompanyLayout).
  if (projectSlug) return <Outlet context={ctx} />;

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: company.name, to: `/c/${company.slug}` },
          { label: ws.name },
        ]}
      />
      <LayoutHeader title={ws.name} />
      <TabBar
        tabs={[
          { to: base, label: 'Overview', end: true },
          { to: `${base}/activity`, label: 'Activity' },
          { to: `${base}/deliverables`, label: 'Deliverables' },
          ...(isStaff ? [{ to: `${base}/context`, label: 'Context' }] : []),
          ...(isStaff || role === 'ADMIN' ? [{ to: `${base}/knowledge`, label: 'Knowledge' }] : []),
          ...(isStaff ? [{ to: `${base}/connectors`, label: 'Connectors' }] : []),
          ...(isStaff ? [{ to: `${base}/settings`, label: 'Settings' }] : []),
        ]}
      />
      <Outlet context={ctx} />
    </>
  );
}
